import {tokensForThemeProvider} from '@forecast-it/design-system';
import React from 'react';
import styled from 'styled-components';

// 'accent' and 'neutral' are both outlined on the card's own surface - they sit beside an answer
// and must not out-weigh it. 'primary' is filled, for the one control in a card that IS the thing
// to do: a failed turn's way back to a working one, where there is no answer left to out-weigh.
export type NovaCompactButtonTone = 'accent' | 'neutral' | 'primary';

export interface NovaCompactButtonProps {
	onPress: () => void;
	tone?: NovaCompactButtonTone;
	children: React.ReactNode;
	disabled?: boolean;
	// Why the control is refused, when it is. Rendered as the native title so the reason is legible
	// on hover to everybody, the same way the row control states its own.
	title?: string;
	// Passed through because a caller may need to point something at this button - the agent
	// picker positions itself against its own trigger by element id.
	id?: string;
	// Lets a caller extend this button with styled(), for a control that needs its own proportions.
	className?: string;
	'data-cy'?: string;
}

// Optional chaining for the same reason NovaInvocationControl needs it: this module loads
// transitively through the shared web-components barrel, and a test that mocks
// '@forecast-it/design-system' without providing tokensForThemeProvider would otherwise crash on
// import alone, before any test ran.
const ref = tokensForThemeProvider?.sky?.ref?.color;
const sys = tokensForThemeProvider?.sky?.sys?.color;
const size = tokensForThemeProvider?.sky?.sys?.size;

const SURFACE = sys?.background.neutral['1'] ?? '';
const ACCENT_TEXT = sys?.text.content.primary ?? '';
const ACCENT_BORDER = ref?.blue['100'] ?? '';
const NEUTRAL_TEXT = sys?.text.content.auxiliary ?? '';
const NEUTRAL_BORDER = sys?.border.decorative ?? '';
const PRIMARY_SURFACE = ref?.blue['900'] ?? '';
const PRIMARY_TEXT = sys?.text.content.ondark ?? '';
const DISABLED_TEXT = sys?.text.disabled.ondisabled ?? '';
const DISABLED_SURFACE = sys?.background.disabled['1'] ?? '';
const RADIUS = size?.borderradius['3'] ?? '';
const FOCUS_RING = sys?.border.focusring ?? '';

// Every action that sits inside a Nova answer card - Copy, Reply to Nova, Change agent, Discard,
// Try again - is this one size. A row of controls that act on the same answer has no reason to
// rank them by height, and a taller pair among them read as a different kind of control than the
// Copy beside it. They are deliberately smaller than a design-system Button, which is 44px tall and
// carries its own emphasis palette - three of those in a card would out-weigh the answer they act
// on. Everything here is a design-system token; only the proportions are this component's own.
const CompactButton = styled.button<{tone: NovaCompactButtonTone}>`
	display: inline-flex;
	align-items: center;
	gap: 4px;
	padding: 3px 8px;
	background: ${props => (props.tone === 'primary' ? PRIMARY_SURFACE : SURFACE)};
	// The filled tone draws no line, but still reserves one: carrying a transparent border instead of
	// none keeps every tone's box identical, so a row mixing them lines up and the taller variant
	// below can raise them all by the same amount without the filled one coming out short.
	border: 1px solid
		${props => (props.tone === 'primary' ? 'transparent' : props.tone === 'accent' ? ACCENT_BORDER : NEUTRAL_BORDER)};
	border-radius: ${RADIUS};
	color: ${props => (props.tone === 'primary' ? PRIMARY_TEXT : props.tone === 'accent' ? ACCENT_TEXT : NEUTRAL_TEXT)};
	font: inherit;
	font-size: 12px;
	font-weight: 500;
	line-height: 16px;
	cursor: pointer;

	&:disabled {
		// The filled tone has to drop its fill as well as its text colour: keeping the emphasis
		// colour under disabled text reads as a control that is merely slow to respond.
		background: ${props => (props.tone === 'primary' ? DISABLED_SURFACE : SURFACE)};
		color: ${DISABLED_TEXT};
		// Every tone, including the filled one, whose border is transparent until here. A refused
		// control states the same thing however it is drawn when live, so all three come out as one
		// shape rather than the filled one losing its edge and reading as a label.
		border-color: ${NEUTRAL_BORDER};
		cursor: default;
	}

	&:focus-visible {
		outline: 2px solid ${FOCUS_RING};
		outline-offset: 1px;
	}
`;

export const NovaCompactButton = (props: NovaCompactButtonProps): JSX.Element => (
	<CompactButton
		type="button"
		tone={props.tone ?? 'accent'}
		disabled={props.disabled}
		title={props.title}
		onClick={props.onPress}
		id={props.id}
		className={props.className}
		data-cy={props['data-cy']}
	>
		{props.children}
	</CompactButton>
);
